const gluestackPlugin = require('@gluestack-ui/nativewind-utils/tailwind-plugin');

/**
 * PRIMUM DESIGN SYSTEM - Tailwind Configuration
 *
 * Design system tokens and framework integration for consistent UI.
 *
 * • Colors: CSS variables for light/dark theming (see constants/colors.ts)
 * • Typography: Inter font family with semantic naming (font-heading, font-body)
 * • Shadows: Hard/soft variants for depth (shadow-hard-2, shadow-soft-1)
 * • Integration: Gluestack UI + NativeWind for React Native compatibility
 */

/** @type {import('tailwindcss').Config} */
module.exports = {
  darkMode: 'class',
  // `constants` is scanned because shared class strings live there; a class
  // defined outside these globs is silently dropped from the generated CSS.
  content: [
    './app/**/*.{tsx,jsx,ts,js}',
    './components/**/*.{tsx,jsx,ts,js}',
    './constants/**/*.{tsx,jsx,ts,js}',
    // Class names also live in shared helpers here — `accessibleTouchTarget`
    // hands out a min-height class that was silently never compiled while this
    // directory went unscanned, so every call site asking for a 48px target got
    // nothing.
    './util/**/*.{tsx,jsx,ts,js}',
    // Feature folders own their UI, so a class written only there would
    // otherwise never be compiled.
    './features/**/*.{tsx,jsx,ts,js}',
  ],
  presets: [require('nativewind/preset')],
  safelist: [
    {
      pattern:
        /(bg|border|text|stroke|fill)-(primary|secondary|tertiary|error|success|warning|info|typography|outline|background|highlight|mark)-(0|50|100|200|300|400|500|600|700|800|900|950|white|gray|black|error|warning|muted|success|info|light|dark)/,
    },
  ],
  theme: {
    colors: {
      inherit: 'inherit',
      current: 'currentColor',
      transparent: 'transparent',
      black: '#000000',
      white: '#FFFFFF',
      gray: '#8B8680',
      mark: {
        0: 'var(--color-mark-0)',
        50: 'var(--color-mark-50)',
        100: 'var(--color-mark-100)',
        200: 'var(--color-mark-200)',
        300: 'var(--color-mark-300)',
        400: 'var(--color-mark-400)',
        500: 'var(--color-mark-500)',
        600: 'var(--color-mark-600)',
        700: 'var(--color-mark-700)',
        800: 'var(--color-mark-800)',
        900: 'var(--color-mark-900)',
        950: 'var(--color-mark-950)',
      },
      primary: {
        0: 'var(--color-primary-0)',
        50: 'var(--color-primary-50)',
        100: 'var(--color-primary-100)',
        200: 'var(--color-primary-200)',
        300: 'var(--color-primary-300)',
        400: 'var(--color-primary-400)',
        500: 'var(--color-primary-500)',
        600: 'var(--color-primary-600)',
        700: 'var(--color-primary-700)',
        800: 'var(--color-primary-800)',
        900: 'var(--color-primary-900)',
        950: 'var(--color-primary-950)',
      },
      secondary: {
        0: 'var(--color-secondary-0)',
        50: 'var(--color-secondary-50)',
        100: 'var(--color-secondary-100)',
        200: 'var(--color-secondary-200)',
        300: 'var(--color-secondary-300)',
        400: 'var(--color-secondary-400)',
        500: 'var(--color-secondary-500)',
        600: 'var(--color-secondary-600)',
        700: 'var(--color-secondary-700)',
        800: 'var(--color-secondary-800)',
        900: 'var(--color-secondary-900)',
        950: 'var(--color-secondary-950)',
      },
      tertiary: {
        0: 'var(--color-tertiary-0)',
        50: 'var(--color-tertiary-50)',
        100: 'var(--color-tertiary-100)',
        200: 'var(--color-tertiary-200)',
        300: 'var(--color-tertiary-300)',
        400: 'var(--color-tertiary-400)',
        500: 'var(--color-tertiary-500)',
        600: 'var(--color-tertiary-600)',
        700: 'var(--color-tertiary-700)',
        800: 'var(--color-tertiary-800)',
        900: 'var(--color-tertiary-900)',
        950: 'var(--color-tertiary-950)',
      },
      error: {
        0: 'var(--color-error-0)',
        50: 'var(--color-error-50)',
        100: 'var(--color-error-100)',
        200: 'var(--color-error-200)',
        300: 'var(--color-error-300)',
        400: 'var(--color-error-400)',
        500: 'var(--color-error-500)',
        600: 'var(--color-error-600)',
        700: 'var(--color-error-700)',
        800: 'var(--color-error-800)',
        900: 'var(--color-error-900)',
        950: 'var(--color-error-950)',
      },
      success: {
        0: 'var(--color-success-0)',
        50: 'var(--color-success-50)',
        100: 'var(--color-success-100)',
        200: 'var(--color-success-200)',
        300: 'var(--color-success-300)',
        400: 'var(--color-success-400)',
        500: 'var(--color-success-500)',
        600: 'var(--color-success-600)',
        700: 'var(--color-success-700)',
        800: 'var(--color-success-800)',
        900: 'var(--color-success-900)',
        950: 'var(--color-success-950)',
      },
      warning: {
        0: 'var(--color-warning-0)',
        50: 'var(--color-warning-50)',
        100: 'var(--color-warning-100)',
        200: 'var(--color-warning-200)',
        300: 'var(--color-warning-300)',
        400: 'var(--color-warning-400)',
        500: 'var(--color-warning-500)',
        600: 'var(--color-warning-600)',
        700: 'var(--color-warning-700)',
        800: 'var(--color-warning-800)',
        900: 'var(--color-warning-900)',
        950: 'var(--color-warning-950)',
      },
      info: {
        0: 'var(--color-info-0)',
        50: 'var(--color-info-50)',
        100: 'var(--color-info-100)',
        200: 'var(--color-info-200)',
        300: 'var(--color-info-300)',
        400: 'var(--color-info-400)',
        500: 'var(--color-info-500)',
        600: 'var(--color-info-600)',
        700: 'var(--color-info-700)',
        800: 'var(--color-info-800)',
        900: 'var(--color-info-900)',
        950: 'var(--color-info-950)',
      },
      typography: {
        0: 'var(--color-typography-0)',
        50: 'var(--color-typography-50)',
        100: 'var(--color-typography-100)',
        200: 'var(--color-typography-200)',
        300: 'var(--color-typography-300)',
        400: 'var(--color-typography-400)',
        500: 'var(--color-typography-500)',
        600: 'var(--color-typography-600)',
        700: 'var(--color-typography-700)',
        800: 'var(--color-typography-800)',
        900: 'var(--color-typography-900)',
        950: 'var(--color-typography-950)',
        white: 'var(--color-typography-white)',
        gray: 'var(--color-typography-gray)',
        black: 'var(--color-typography-black)',
      },
      outline: {
        0: 'var(--color-outline-0)',
        50: 'var(--color-outline-50)',
        100: 'var(--color-outline-100)',
        200: 'var(--color-outline-200)',
        300: 'var(--color-outline-300)',
        400: 'var(--color-outline-400)',
        500: 'var(--color-outline-500)',
        600: 'var(--color-outline-600)',
        700: 'var(--color-outline-700)',
        800: 'var(--color-outline-800)',
        900: 'var(--color-outline-900)',
        950: 'var(--color-outline-950)',
      },
      background: {
        0: 'var(--color-background-0)',
        50: 'var(--color-background-50)',
        100: 'var(--color-background-100)',
        200: 'var(--color-background-200)',
        300: 'var(--color-background-300)',
        400: 'var(--color-background-400)',
        500: 'var(--color-background-500)',
        600: 'var(--color-background-600)',
        700: 'var(--color-background-700)',
        800: 'var(--color-background-800)',
        900: 'var(--color-background-900)',
        950: 'var(--color-background-950)',
        error: 'var(--color-background-error)',
        warning: 'var(--color-background-warning)',
        muted: 'var(--color-background-muted)',
        success: 'var(--color-background-success)',
        info: 'var(--color-background-info)',
        light: 'var(--color-background-light)',
        alice: 'var(--color-background-alice)',
        dark: 'var(--color-background-dark)',
      },
      indicator: {
        primary: 'var(--color-indicator-primary)',
        info: 'var(--color-indicator-info)',
        error: 'var(--color-indicator-error)',
      },
      highlight: {
        0: 'var(--color-highlight-0)',
        50: 'var(--color-highlight-50)',
        100: 'var(--color-highlight-100)',
        200: 'var(--color-highlight-200)',
        300: 'var(--color-highlight-300)',
        400: 'var(--color-highlight-400)',
        500: 'var(--color-highlight-500)',
        600: 'var(--color-highlight-600)',
        700: 'var(--color-highlight-700)',
        800: 'var(--color-highlight-800)',
        900: 'var(--color-highlight-900)',
        950: 'var(--color-highlight-950)',
      },
    },
    extend: {
      fontFamily: {
        sans: ['Inter-Regular', 'ui-sans-serif', 'system-ui', 'sans-serif'],
        thin: ['Inter-Thin', 'sans-serif'],
        extralight: ['Inter-ExtraLight', 'sans-serif'],
        light: ['Inter-Light', 'sans-serif'],
        normal: ['Inter-Regular', 'sans-serif'],
        body: ['Inter-Regular', 'sans-serif'],
        roboto: ['Inter-Regular', 'sans-serif'],
        medium: ['Inter-Medium', 'sans-serif'],
        semibold: ['Inter-SemiBold', 'sans-serif'],
        bold: ['Inter-Bold', 'sans-serif'],
        heading: ['Inter-Bold', 'sans-serif'],
        extrabold: ['Inter-ExtraBold', 'sans-serif'],
        black: ['Inter-Black', 'sans-serif'],
      },
      fontWeight: {
        thin: '100',
        extralight: '200',
        light: '300',
        normal: '400',
        body: '400',
        roboto: '400',
        medium: '500',
        semibold: '600',
        bold: '700',
        heading: '700',
        extrabold: '800',
        black: '900',
      },
      fontSize: {
        '2xs': ['10px', { lineHeight: '1' }],
      },
      keyframes: {
        'fade-in': {
          from: { opacity: '0' },
          to: { opacity: '1' },
        },
        'fade-in-up': {
          from: { opacity: '0', transform: 'translateY(8px)' },
          to: { opacity: '1', transform: 'translateY(0)' },
        },
        'scale-in': {
          from: { opacity: '0', transform: 'scale(0.9)' },
          to: { opacity: '1', transform: 'scale(1)' },
        },
      },
      animation: {
        'fade-in': 'fade-in 300ms ease-out',
        'fade-in-up': 'fade-in-up 400ms ease-out',
        'scale-in': 'scale-in 300ms ease-out',
      },
      boxShadow: {
        'hard-1': '-2px 2px 8px 0px rgba(38, 38, 38, 0.20)',
        'hard-2': '0px 3px 10px 0px rgba(38, 38, 38, 0.20)',
        'hard-3': '2px 2px 8px 0px rgba(38, 38, 38, 0.20)',
        'hard-4': '0px -3px 10px 0px rgba(38, 38, 38, 0.20)',
        'hard-5': '0px 2px 10px 0px rgba(38, 38, 38, 0.10)',
        'soft-1': '0px 0px 10px rgba(38, 38, 38, 0.1)',
        'soft-2': '0px 0px 20px rgba(38, 38, 38, 0.2)',
        'soft-3': '0px 0px 30px rgba(38, 38, 38, 0.1)',
        'soft-4': '0px 0px 40px rgba(38, 38, 38, 0.1)',
        'bottom-sm': '0px 2px 4px 0px rgba(38, 38, 38, 0.08)',
      },
    },
  },
  plugins: [gluestackPlugin],
};
